import { useCallback, useEffect, useMemo, useState } from 'react'

import { Heading, Button, FixedBanner } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import { useOnChainTransfer } from '@modals/blockchain'
import { routes } from '@services/sections/modal'
import { GraphQLErrorBoundary } from '@shared/components/errors'
import { Center } from '@shared/components/layout'
import { useAceBlockchainDepositAddressQuery } from '@shared/graphql'
import { useConnectWallet } from '@shared/wallet/hooks/useConnectWallet'
import { useTokenMessenger } from '@shared/wallet/hooks/useTokenMessenger'
import { useUsdcAllowance } from '@shared/wallet/hooks/useUsdcAllowance'
import { useUsdcApproval } from '@shared/wallet/hooks/useUsdcApproval'
import { isHex } from '@shared/wallet/lib/type-guards'
import useTranslation from 'next-translate/useTranslation'

import { AceDepositInfoBanner, FormattedWalletAddress } from '../../components'
import { ConnectRecipientWallet } from '../../containers'

import type { SupportedChainId } from '@shared/wallet/constants/chain'

export interface CompleteDepositContentProps {
  /**
   * Chain ID.
   */
  chainId: SupportedChainId
}

export const CompleteDepositContent: React.FC<CompleteDepositContentProps> = ({
  chainId,
}) => {
  const { t } = useTranslation('ace')
  const [{ destination, amount }] = useOnChainTransfer()
  const { router } = useModal()

  const { isConnected, address } = useConnectWallet()
  const {
    depositForBurn,
    isPending: isDepositLoading,
    isSuccess: isDepositSuccess,
    error: depositError,
  } = useTokenMessenger(chainId)
  const {
    approve,
    isPending: isApprovalLoading,
    isSuccess: isApprovalSuccess,
    error: approvalError,
  } = useUsdcApproval(chainId)
  const { data: allowance, isLoading: isAllowanceLoading } = useUsdcAllowance(
    chainId,
    destination?.address ?? address,
  )
  const {
    data: aceDepositAddress,
    loading: isAceDepositAddressLoading,
    error: aceDepositAddressError,
    refetch: refetchAceDepositAddress,
  } = useAceBlockchainDepositAddressQuery()

  const isEnoughAllowance = useMemo(
    () =>
      amount != null && !isAllowanceLoading && !isApprovalLoading
        ? amount <= Number(allowance)
        : false,
    [amount, isAllowanceLoading, isApprovalLoading, allowance],
  )

  const [isWalletConnectionError, setIsWalletConnectionError] = useState(false)

  const title = useMemo(() => {
    if (isConnected) {
      return isEnoughAllowance
        ? t('deposit.completeDeposit.deposit.title')
        : t('deposit.completeDeposit.approval.title')
    }
    return t('deposit.completeDeposit.disconnect.title')
  }, [isConnected, isEnoughAllowance, t])

  const description = useMemo(() => {
    if (isConnected) {
      return isEnoughAllowance
        ? t('deposit.completeDeposit.deposit.description')
        : t('deposit.completeDeposit.approval.description')
    }
    return t('deposit.completeDeposit.disconnect.description')
  }, [isConnected, isEnoughAllowance, t])
  const cta = useMemo(
    () =>
      isEnoughAllowance
        ? t('deposit.completeDeposit.deposit.cta')
        : t('deposit.completeDeposit.approval.cta'),
    [isEnoughAllowance, t],
  )

  const handleApprove = useCallback(() => {
    if (amount != null) {
      approve(amount)
    }
  }, [amount, approve])

  const handleDeposit = useCallback(() => {
    const depositAddress =
      aceDepositAddress?.aceBlockchainDepositAddress?.address

    if (amount != null && isHex(depositAddress)) {
      depositForBurn(amount, depositAddress)
    }
  }, [
    aceDepositAddress?.aceBlockchainDepositAddress?.address,
    amount,
    depositForBurn,
  ])

  const handleCtaClick = useCallback(() => {
    if (isEnoughAllowance) {
      handleDeposit()
    } else {
      handleApprove()
    }
  }, [handleApprove, handleDeposit, isEnoughAllowance])

  const handleWalletConnectionError = useCallback(
    () => setIsWalletConnectionError(true),
    [],
  )

  const handleWalletConnectionSuccess = useCallback(
    () => setIsWalletConnectionError(false),
    [],
  )

  const isLoading =
    isApprovalLoading ||
    isAllowanceLoading ||
    isDepositLoading ||
    (isApprovalSuccess && !isEnoughAllowance) ||
    isAceDepositAddressLoading

  useEffect(() => {
    if (isDepositSuccess) {
      router.push(routes.transfer.wallet.depositSuccess)
    }
  }, [isDepositSuccess, router])

  return (
    <GraphQLErrorBoundary
      error={aceDepositAddressError}
      retry={refetchAceDepositAddress}
    >
      <Heading alignment="center" layout="vertical" variant="page/sm">
        <Heading.Header as="h2">{title}</Heading.Header>
        <Heading.Intro as="p">{description}</Heading.Intro>
      </Heading>
      <Center variant="horizontal">
        <div className="mt-12 w-128">
          {destination != null && (
            <>
              {!isConnected && (
                <FormattedWalletAddress
                  address={destination.address}
                  chain={destination.chain}
                  heading={t('deposit.completeDeposit.connectedWalletLabel')}
                  walletName={destination.description ?? undefined}
                />
              )}

              <ConnectRecipientWallet
                address={destination.address}
                chain={destination.chain}
                label={t('deposit.completeDeposit.connectedWalletLabel')}
                onError={handleWalletConnectionError}
                onSuccess={handleWalletConnectionSuccess}
              />
            </>
          )}

          {isConnected && (
            <>
              {isEnoughAllowance && (
                <AceDepositInfoBanner
                  blockchain={destination?.chain}
                  className="mt-6"
                />
              )}
              <Button
                className="mt-6 w-full"
                disabled={isWalletConnectionError}
                loading={isLoading}
                onClick={handleCtaClick}
                variant="primary"
              >
                {cta}
              </Button>
              {(depositError || approvalError) && (
                <FixedBanner className="mt-6" status="error">
                  <FixedBanner.Title>
                    {t('alert.aceDepositError.title')}
                  </FixedBanner.Title>
                  <FixedBanner.Description>
                    {t('alert.aceDepositError.description')}
                  </FixedBanner.Description>
                </FixedBanner>
              )}
            </>
          )}
        </div>
      </Center>
    </GraphQLErrorBoundary>
  )
}
